import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Home, Sparkles, ImagePlus, X, Star, Loader2, Search, ChevronDown, MapPin, Globe, Users, Lock, Check, Tag, SlidersHorizontal } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DevProjectPicker } from "@/components/DevProjectPicker";
import { PostPublishDistribution } from "@/components/PostPublishDistribution";
import { toast } from "sonner";

// M3C controlled vocabularies (shared with HaveBuyer for engine string-matching).
const COMPLETION_OPTIONS = ["Pre-selling", "Under Construction", "Ready for Occupancy (RFO)", "Resale / Existing"];
import { AMENITIES, normalizeAmenities, PROPERTY_TYPE_GROUPS, propertyTypeLabel, SALE_TYPES, saleTypeFormLabel } from "@/lib/taxonomy";
const NONE = "__none__";

/** Compact single-select PSGC location picker (administrative geography). */
const PsgcSingleLocation = ({ code, name, onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/search`, { params: { q: q.trim(), limit: 12 } });
        setResults(data.results || []); setOpen(true);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(timer.current);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={boxRef} className="relative" data-testid="property-location-picker">
      {name ? (
        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-white px-3 h-11" data-testid="property-location-current">
          <MapPin className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium text-slate-700 truncate">{name}</span>
          {code && <span className="text-[10px] font-mono text-slate-400">{code}</span>}
          <button type="button" data-testid="property-location-change" onClick={() => onPick({ code: "", name: "" })} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Change</button>
        </div>
      ) : (
        <div className="relative mt-1.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="property-location-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
            placeholder="Search a city or municipality…"
            className="w-full h-11 pl-9 pr-9 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
        </div>
      )}
      {open && !name && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-border bg-white shadow-lg" data-testid="property-location-results">
          {results.map((r) => (
            <li key={r.code}>
              <button type="button" data-testid={`ploc-option-${r.code}`} onClick={() => { onPick({ code: r.code, name: r.name }); setQ(""); setResults([]); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50">
                <span className="truncate">{r.name}{r.parentName ? <span className="text-slate-400 font-normal">, {r.parentName}</span> : null}</span>
                <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.level === "CITY_MUNICIPALITY" ? "City / Municipality" : r.level === "PROVINCE" ? "Province" : "Region"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function HaveProperty() {
  const nav = useNavigate();
  const { user } = useAuth();
  // Mirror backend Unit Hosting eligibility (_validate_listing): ADMIN is exempt; otherwise
  // must be an active, verified Property Manager / Licensed Broker.
  const canHost = user && (user.role === "ADMIN" || (
    (user.account_status || "ACTIVE") === "ACTIVE"
    && user.verification_status === "PROFESSIONAL_VERIFIED"
    && ["PROPERTY_MANAGER", "LICENSED_BROKER"].includes(user.role)
  ));
  // Consumer/professional-facing label for the canonical "Unit Hosting" listing type.
  const LT_DISPLAY = { "For Sale": "For Sale", "For Rent": "For Rent", "Unit Hosting": "Stays" };
  const [sp] = useSearchParams();
  const editId = sp.get("edit") || "";
  const fileRef = useRef();
  const [saving, setSaving] = useState(false);
  const [draftId, setDraftId] = useState(editId || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [distrib, setDistrib] = useState(null); // post-publish distribution target
  const [uploading, setUploading] = useState(false);
  const [loadingRec, setLoadingRec] = useState(!!editId);
  const [showMore, setShowMore] = useState(true);
  const [showPromo, setShowPromo] = useState(!editId);
  const [photos, setPhotos] = useState([]); // {path, url}
  const [f, setF] = useState({
    title: "", property_type: "Condominium", listing_type: "For Sale", sale_type: "", city: "", location_code: "",
    neighborhood: "", project: "", bedrooms: "", bathrooms: "1", parking_slots: "",
    floor_area_sqm: "", lot_area_sqm: "", price: "", furnishing: "Bare", completion_status: "",
    amenities: [], description: "",
    nightly_rate: "", max_guests: "2", beds: "1", minimum_stay_nights: "1",
    availability_status: "AVAILABLE", check_in_time: "", check_out_time: "", house_rules: "",
    // Phase 1: structured development + optional payment/promo
    developer_id: null, developer_name: "", project_id: null, project_name: "",
    promo_reservation_fee: "", promo_monthly_dp: "", promo_dp_term: "",
    promo_price: "", promo_valid_until: "", promo_terms: "",
    // P0/P1: creation-time visibility. Eligible new listings default to Public Marketplace.
    visibility: "PUBLIC_MARKETPLACE",
  });
  const isHosting = f.listing_type === "Unit Hosting";
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Collapsed-state cues — reflect ONLY real, user-populated info (never defaults / merely opening a section).
  const hasPromo = ["promo_reservation_fee", "promo_monthly_dp", "promo_dp_term", "promo_price", "promo_valid_until", "promo_terms"]
    .some((k) => String(f[k] ?? "").trim() !== "");
  const detailsCount = [
    f.neighborhood.trim() !== "",
    f.bathrooms !== "1",            // "1" is the default — don't count unless changed
    f.furnishing !== "Bare",        // "Bare" is the default — don't count unless changed
    String(f.floor_area_sqm).trim() !== "",
    String(f.lot_area_sqm).trim() !== "",
    (f.amenities?.length || 0) > 0,
  ].filter(Boolean).length;

  // ---- Listing Assistant (Phase 1): Generate -> Preview -> Apply. Never auto-saves. ----
  const [aiBusy, setAiBusy] = useState("");
  const [aiPanel, setAiPanel] = useState(null); // {task, title?, description?, selling_points?}
  const buildFacts = () => ({
    property_type: f.property_type, listing_type: f.listing_type, city: f.city,
    neighborhood: f.neighborhood, bedrooms: f.bedrooms, bathrooms: f.bathrooms,
    parking_slots: f.parking_slots, floor_area_sqm: f.floor_area_sqm, lot_area_sqm: f.lot_area_sqm,
    price: f.price, furnishing: f.furnishing, completion_status: f.completion_status,
    amenities: f.amenities, developer_name: f.developer_name, project_name: f.project_name,
    availability_status: f.availability_status,
    // Phase 2 (Smart Titles): For-Sale payment facts — used only for the title value-hook.
    payment_promo: f.listing_type === "For Sale" ? {
      reservation_fee: f.promo_reservation_fee === "" ? null : parseInt(f.promo_reservation_fee),
      monthly_downpayment_amount: f.promo_monthly_dp === "" ? null : parseFloat(f.promo_monthly_dp),
      dp_term_months: f.promo_dp_term === "" ? null : parseInt(f.promo_dp_term),
      promo_price: f.promo_price === "" ? null : parseInt(f.promo_price),
      promo_valid_until: f.promo_valid_until || null,
    } : null,
  });
  const runAssist = async (task) => {
    setAiBusy(task); setAiPanel(null);
    try {
      const { data } = await api.post("/ai/listing/assist", {
        task, facts: buildFacts(), free_text: f.description || "", property_id: editId || "",
      });
      if (!data.ok) { toast.message(data.disclaimer || "AI is unavailable right now — keep editing normally."); return; }
      setAiPanel({ task, ...data });
    } catch (e) {
      toast.message("AI is unavailable right now — you can keep editing your listing normally.");
    } finally { setAiBusy(""); }
  };
  const AiBtn = ({ task, label }) => (
    <button type="button" data-testid={`ai-${task}`} onClick={() => runAssist(task)} disabled={!!aiBusy}
      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-50 transition-colors">
      {aiBusy === task ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {label}
    </button>
  );

  const toggle = (v) => setF((p) => ({ ...p, amenities: p.amenities.includes(v) ? p.amenities.filter((x) => x !== v) : [...p.amenities, v] }));

  // Phase 1: /have-property?type=Unit+Hosting preselects Stays for eligible hosts.
  // Coerce back to For Sale if the type is Unit Hosting but the user is not eligible.
  useEffect(() => {
    if (editId) return;
    const t = sp.get("type");
    if (t === "Unit Hosting" && canHost) set("listing_type", "Unit Hosting");
    else if (!canHost && f.listing_type === "Unit Hosting") set("listing_type", "For Sale");
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/properties/${editId}`);
        setF({
          title: data.title || "", property_type: data.property_type || "Condominium",
          listing_type: data.listing_type || "For Sale", sale_type: data.sale_type || "", city: data.city || "", location_code: data.location_code || "",
          neighborhood: data.neighborhood || "", project: data.project || "",
          bedrooms: String(data.bedrooms ?? 0), bathrooms: String(data.bathrooms ?? 0),
          parking_slots: data.parking_slots === null || data.parking_slots === undefined ? "" : String(data.parking_slots),
          floor_area_sqm: data.floor_area_sqm ? String(data.floor_area_sqm) : "",
          lot_area_sqm: data.lot_area_sqm ? String(data.lot_area_sqm) : "",
          price: data.price ? String(data.price) : "", furnishing: data.furnishing || "Bare",
          completion_status: data.completion_status || "",
          amenities: normalizeAmenities(data.amenities && data.amenities.length ? data.amenities : (data.features || [])),
          description: data.description || "",
          nightly_rate: data.nightly_rate ? String(data.nightly_rate) : "",
          max_guests: String(data.max_guests ?? 2), beds: String(data.beds ?? 1),
          minimum_stay_nights: String(data.minimum_stay_nights ?? 1),
          availability_status: data.availability_status || "AVAILABLE",
          check_in_time: data.check_in_time || "", check_out_time: data.check_out_time || "",
          house_rules: data.house_rules || "",
          developer_id: data.developer_id || null, developer_name: data.developer_name || "",
          project_id: data.project_id || null, project_name: data.project_name || data.project || "",
          promo_reservation_fee: data.payment_promo?.reservation_fee != null ? String(data.payment_promo.reservation_fee) : "",
          promo_monthly_dp: data.payment_promo?.monthly_downpayment_amount != null ? String(data.payment_promo.monthly_downpayment_amount) : "",
          promo_dp_term: data.payment_promo?.dp_term_months != null ? String(data.payment_promo.dp_term_months) : "",
          promo_price: data.payment_promo?.promo_price != null ? String(data.payment_promo.promo_price) : "",
          promo_valid_until: data.payment_promo?.promo_valid_until || "",
          promo_terms: data.payment_promo?.terms || "",
        });
        if (data.payment_promo) setShowPromo(true);
      } catch (e) { toast.error(apiError(e)); nav("/my-properties"); }
      finally { setLoadingRec(false); }
    })();
  }, [editId]);

  const onFiles = async (files) => {
    const arr = Array.from(files).slice(0, 10 - photos.length);
    setUploading(true);
    for (const file of arr) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds the 10 MB limit`); continue; }
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post("/upload/temp-photo", fd); setPhotos((p) => [...p, data]); }
      catch (e) { toast.error(apiError(e)); }
    }
    setUploading(false);
  };

  const buildPayload = () => {
      const payload = {
        title: f.title, property_type: f.property_type, listing_type: f.listing_type,
        sale_type: f.listing_type === "For Sale" ? (f.sale_type || null) : null,
        city: f.city, location_code: f.location_code, neighborhood: f.neighborhood,
        project: f.project_name || "",
        developer_id: f.developer_id || null, developer_name: f.developer_name || null,
        project_id: f.project_id || null, project_name: f.project_name || null,
        bedrooms: parseInt(f.bedrooms || 0), bathrooms: parseInt(f.bathrooms || 0),
        parking_slots: f.parking_slots === "" ? null : parseInt(f.parking_slots),
        floor_area_sqm: parseFloat(f.floor_area_sqm || 0) || 0, lot_area_sqm: parseFloat(f.lot_area_sqm || 0) || 0,
        price: parseInt(f.price || 0), furnishing: f.furnishing,
        completion_status: f.completion_status, amenities: f.amenities, features: f.amenities,
        description: f.description,
        nightly_rate: parseInt(f.nightly_rate || 0), max_guests: parseInt(f.max_guests || 0),
        beds: parseInt(f.beds || 0), minimum_stay_nights: parseInt(f.minimum_stay_nights || 1),
        availability_status: f.availability_status,
        check_in_time: f.check_in_time, check_out_time: f.check_out_time, house_rules: f.house_rules,
      };
      const promoObj = {
        reservation_fee: f.promo_reservation_fee === "" ? null : parseInt(f.promo_reservation_fee),
        monthly_downpayment_amount: f.promo_monthly_dp === "" ? null : parseFloat(f.promo_monthly_dp),
        dp_term_months: f.promo_dp_term === "" ? null : parseInt(f.promo_dp_term),
        promo_price: f.promo_price === "" ? null : parseInt(f.promo_price),
        promo_valid_until: f.promo_valid_until || null,
        terms: f.promo_terms ? f.promo_terms : null,
      };
      const hasPromo = Object.values(promoObj).some((v) => v !== null);
      payload.payment_promo = (f.listing_type === "For Sale" && hasPromo) ? promoObj : null;
      if (!isHosting) {
        ["nightly_rate", "max_guests", "beds", "minimum_stay_nights", "availability_status", "check_in_time", "check_out_time", "house_rules"].forEach((k) => delete payload[k]);
      }
      return payload;
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/properties/draft", {
        ...buildPayload(), id: draftId || null,
        photo_paths: photos.map((p) => p.path), visibility: f.visibility,
      });
      setDraftId(data.id);
      setDraftSavedAt(data.updated_at || new Date().toISOString());
      toast.success("Draft saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingDraft(false); }
  };

  const submit = async () => {
    if (!f.title) { toast.error("Add a listing title."); return; }
    if (!f.city) { toast.error("Pick a property location (city / municipality)."); return; }
    if (isHosting) {
      if (!f.nightly_rate || parseInt(f.nightly_rate) <= 0) { toast.error("Add a nightly rate."); return; }
      if (parseInt(f.max_guests || 0) < 1) { toast.error("Max guests must be at least 1."); return; }
      if (parseInt(f.minimum_stay_nights || 0) < 1) { toast.error("Minimum stay must be at least 1 night."); return; }
    } else if (!f.price) { toast.error("Add a price."); return; }
    if (!isHosting && f.listing_type === "For Sale" && !editId && !f.sale_type) {
      toast.error("Choose a Sale Type — Developer, Resale, Pasalo or Foreclosed."); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const publishId = editId || draftId;  // publishing a continued draft activates it
      if (publishId) {
        const { data } = await api.put(`/properties/${publishId}`, payload);
        toast.success("Listing updated");
        nav(`/property/${data.id}/matches`);
      } else {
        const { data } = await api.post("/properties", { ...payload, photo_paths: photos.map((p) => p.path), visibility: f.visibility });
        toast.success("Listing saved — finding buyers…");
        setSaving(false);
        setDistrib({ id: data.id });
      }
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };

  if (loadingRec) return <PageShell testid="have-property-page"><div className="flex items-center gap-2 text-slate-500 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div></PageShell>;

  return (
    <PageShell testid="have-property-page">
      <Fade>
        <div className="mb-6">
          <p className="eyebrow flex items-center gap-2"><Home className="h-4 w-4" /> {editId ? "Edit Property" : "I Have a Property"}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">{editId ? "Update your listing" : "List a property to match"}</h1>
          <p className="text-slate-500 mt-1">We'll surface buyers in the network who are looking for exactly this.</p>
        </div>
      </Fade>
      <Fade delay={0.05}>
        <div className="bg-slate-50 border border-border rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-5 max-w-4xl">
          {/* Photos (create only manages here; edit manages photos on the detail page) */}
          {!editId && (
            <div className="md:col-span-2">
              <Label>Photos <span className="text-slate-400 font-normal">(JPG/PNG/WEBP, up to 10 · max 10 MB each · first is the cover)</span></Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {photos.map((p, idx) => (
                  <div key={p.path} className="relative h-24 w-32 rounded-lg overflow-hidden border border-border group">
                    <img src={p.url} alt="" className="h-full w-full object-cover" />
                    {idx === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-semibold bg-accent text-accent-foreground px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Cover</span>}
                    {idx !== 0 && <button type="button" onClick={() => setPhotos((ps) => [p, ...ps.filter((x) => x.path !== p.path)])} className="absolute bottom-1 left-1 text-[10px] font-semibold bg-white/90 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100">Make cover</button>}
                    <button type="button" data-testid={`remove-photo-${idx}`} onClick={() => setPhotos((ps) => ps.filter((x) => x.path !== p.path))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-[#0A1B33]/60 text-white flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button type="button" data-testid="upload-photos-btn" onClick={() => fileRef.current?.click()} disabled={uploading || photos.length >= 10}
                  className="h-24 w-32 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-slate-400 hover:border-accent hover:text-accent transition-colors">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ImagePlus className="h-5 w-5" /><span className="text-[11px] mt-1">Add photos</span></>}
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && onFiles(e.target.files)} />
              </div>
            </div>
          )}

          {/* ---- PRIMARY ---- */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between"><Label>Listing title</Label><AiBtn task="title" label="Improve title" /></div>
            <Input data-testid="input-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. 2BR Corner Unit at Uptown Parksuites" className="mt-1.5" />
            {aiPanel?.task === "title" && Array.isArray(aiPanel.titles) && (
              <div data-testid="ai-panel-title" className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-primary/80 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Title Suggestions</p>
                <div className="mt-2 space-y-2">
                  {aiPanel.titles.map((t, i) => (
                    <div key={i} data-testid={`ai-title-card-${t.strategy}`} className="rounded-lg border border-border bg-white px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t.label}</p>
                      <p className="text-sm text-slate-800 mt-0.5">{t.title}</p>
                      <button type="button" data-testid={`ai-use-title-${t.strategy}`} onClick={() => { set("title", t.title); setAiPanel(null); }}
                        className="mt-1.5 text-xs font-semibold text-primary hover:underline">Use this title</button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <button type="button" data-testid="ai-dismiss-title" onClick={() => setAiPanel(null)} className="text-xs text-slate-500 hover:text-slate-700">Dismiss</button>
                  <span className="text-[11px] text-slate-400 ml-auto">AI-assisted draft — review before publishing. You can edit the chosen title before saving.</span>
                </div>
              </div>
            )}
          </div>
          <div>
            <Label>Transaction</Label>
            <Select value={f.listing_type} onValueChange={(v) => set("listing_type", v)}><SelectTrigger data-testid="select-listing-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{["For Sale", "For Rent", ...(canHost ? ["Unit Hosting"] : [])].map((t) => <SelectItem key={t} value={t}>{LT_DISPLAY[t]}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label>Property type</Label>
            <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger data-testid="select-property-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}</SelectContent></Select>
          </div>
          {f.listing_type === "For Sale" && (
            <div>
              <Label>Sale type</Label>
              <Select value={f.sale_type || undefined} onValueChange={(v) => set("sale_type", v)}>
                <SelectTrigger data-testid="select-sale-type" className="mt-1.5"><SelectValue placeholder="Select how it's being sold" /></SelectTrigger>
                <SelectContent>{SALE_TYPES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`sale-type-option-${s.value}`}>{s.formLabel}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-slate-500">How this property is being offered. Required for new listings.</p>
            </div>
          )}
          {/* ---- DEVELOPMENT (Phase 1 · optional, structured) ---- */}
          <div>
            <Label>Developer <span className="text-slate-400 font-normal">(optional)</span></Label>
            <DevProjectPicker kind="developer" testid="developer-picker"
              value={{ id: f.developer_id, name: f.developer_name }}
              onChange={({ id, name }) => setF((p) => ({ ...p, developer_id: id, developer_name: name || "", project_id: null, project_name: "" }))} />
          </div>
          <div>
            <Label>Project / Development <span className="text-slate-400 font-normal">(optional)</span></Label>
            <DevProjectPicker kind="project" testid="project-picker" developerId={f.developer_id}
              value={{ id: f.project_id, name: f.project_name }}
              onChange={({ id, name }) => setF((p) => ({ ...p, project_id: id, project_name: name || "" }))} />
          </div>

          <div className="md:col-span-2">
            <Label>Location <span className="text-slate-400 font-normal">(city / municipality)</span></Label>
            <PsgcSingleLocation code={f.location_code} name={f.city} onPick={({ code, name }) => setF((p) => ({ ...p, location_code: code, city: name }))} />
          </div>
          <div className="md:col-span-2"><Label>{isHosting ? "Price" : f.listing_type === "For Rent" ? "Monthly Rent (₱)" : "List Price (₱)"}</Label><CurrencyInput data-testid="input-price" value={f.price} onValueChange={(v) => set("price", v)} disabled={isHosting} placeholder={isHosting ? "Not used for Unit Hosting — set nightly rate below" : "e.g. 18,500,000"} className="mt-1.5" /></div>

          {/* ---- PAYMENT & PROMO (Phase 1 · For-Sale only · optional, collapsible) ---- */}
          {f.listing_type === "For Sale" && (
            <div className="md:col-span-2 rounded-xl border border-[#C79A3B]/35 bg-[#C79A3B]/[0.06] overflow-hidden">
              <button type="button" data-testid="toggle-payment-promo" onClick={() => setShowPromo((s) => !s)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#C79A3B]/10 transition-colors">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#C79A3B]/15 text-[#C79A3B] shrink-0"><Tag className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#0F2C59]">Payment &amp; Promo Details <span className="text-[10px] font-bold text-[#C79A3B] uppercase tracking-wide">· Optional</span></span>
                  <span className="block text-xs text-slate-500 mt-0.5">Add payment terms, discounts or promotional details when applicable.</span>
                </span>
                {!showPromo && <span data-testid="promo-state-indicator" className={`shrink-0 text-xs font-semibold ${hasPromo ? "text-[#C79A3B]" : "text-slate-400"}`}>{hasPromo ? "Added ✓" : "Not added"}</span>}
                <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${showPromo ? "rotate-180" : ""}`} />
              </button>
              {showPromo && (
                <div className="grid md:grid-cols-2 gap-4 border-t border-[#C79A3B]/25 bg-white/70 p-5" data-testid="payment-promo-fields">
                  <div><Label>Reservation fee (₱)</Label><CurrencyInput data-testid="input-reservation-fee" value={f.promo_reservation_fee} onValueChange={(v) => set("promo_reservation_fee", v)} placeholder="e.g. 50,000" className="mt-1.5" /></div>
                  <div><Label>Monthly Downpayment (₱)</Label><CurrencyInput data-testid="input-monthly-dp" decimal value={f.promo_monthly_dp} onValueChange={(v) => set("promo_monthly_dp", v)} placeholder="e.g. 23,216.67" className="mt-1.5" /></div>
                  <div><Label>DP term (months)</Label><Input data-testid="input-dp-term" type="number" min="1" max="600" value={f.promo_dp_term} onChange={(e) => set("promo_dp_term", e.target.value)} placeholder="e.g. 36" className="mt-1.5" /></div>
                  <div><Label>Promo price (₱)</Label><CurrencyInput data-testid="input-promo-price" value={f.promo_price} onValueChange={(v) => set("promo_price", v)} placeholder="Must be ≤ List Price" className="mt-1.5" /></div>
                  <div><Label>Promo valid until</Label><Input data-testid="input-promo-valid-until" type="date" value={f.promo_valid_until} onChange={(e) => set("promo_valid_until", e.target.value)} className="mt-1.5" /></div>
                  <div className="md:col-span-2"><Label>Promo / payment terms <span className="text-slate-400 font-normal">(max 500 chars)</span></Label><Textarea data-testid="input-promo-terms" maxLength={500} value={f.promo_terms} onChange={(e) => set("promo_terms", e.target.value)} placeholder="e.g. 15% spot-cash discount, or flexible 36-month DP with the developer." className="mt-1.5" /></div>
                  <div className="md:col-span-2 flex items-center justify-between">
                    <p className="text-xs text-slate-500">Payment and promotional details are provided by the listing professional. Please verify current terms and availability with the property representative.</p>
                    <button type="button" data-testid="clear-promo" onClick={() => setF((p) => ({ ...p, promo_reservation_fee: "", promo_monthly_dp: "", promo_dp_term: "", promo_price: "", promo_valid_until: "", promo_terms: "" }))}
                      className="shrink-0 ml-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600"><X className="h-3.5 w-3.5" /> Clear promo</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isHosting && (
            <div className="md:col-span-2 grid md:grid-cols-2 gap-5 rounded-xl border border-accent/40 bg-accent/5 p-5" data-testid="hosting-fields">
              <div className="md:col-span-2"><p className="text-sm font-semibold text-primary flex items-center gap-2"><Home className="h-4 w-4" /> Stays / Unit Hosting details</p></div>
              <div><Label>Nightly rate (₱/night)</Label><CurrencyInput data-testid="input-nightly-rate" value={f.nightly_rate} onValueChange={(v) => set("nightly_rate", v)} placeholder="3,500" className="mt-1.5" /></div>
              <div><Label>Max guests</Label><Input data-testid="input-max-guests" type="number" min="1" value={f.max_guests} onChange={(e) => set("max_guests", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Beds</Label><Input data-testid="input-beds" type="number" min="0" value={f.beds} onChange={(e) => set("beds", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Minimum stay (nights)</Label><Input data-testid="input-min-stay" type="number" min="1" value={f.minimum_stay_nights} onChange={(e) => set("minimum_stay_nights", e.target.value)} className="mt-1.5" /></div>
              <div>
                <Label>Availability</Label>
                <Select value={f.availability_status} onValueChange={(v) => set("availability_status", v)}><SelectTrigger data-testid="select-availability" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["AVAILABLE", "LIMITED", "TEMPORARILY_UNAVAILABLE"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Check-in</Label><Input data-testid="input-checkin" value={f.check_in_time} onChange={(e) => set("check_in_time", e.target.value)} placeholder="2:00 PM" className="mt-1.5" /></div>
                <div><Label>Check-out</Label><Input data-testid="input-checkout" value={f.check_out_time} onChange={(e) => set("check_out_time", e.target.value)} placeholder="12:00 NN" className="mt-1.5" /></div>
              </div>
              <div className="md:col-span-2"><Label>House rules <span className="text-slate-400 font-normal">(optional)</span></Label><Textarea data-testid="input-house-rules" value={f.house_rules} onChange={(e) => set("house_rules", e.target.value)} placeholder="No smoking, no parties, quiet hours after 10 PM…" className="mt-1.5" /></div>
              <div className="md:col-span-2 text-xs text-slate-500">New Stays listings start private to your network. You can publish them to the public Stays marketplace anytime from the listing.</div>
            </div>
          )}
          <div>
            <Label>Bedrooms</Label>
            <Select value={f.bedrooms || NONE} onValueChange={(v) => set("bedrooms", v === NONE ? "" : v)}><SelectTrigger data-testid="select-bedrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>— Not specified —</SelectItem>{["0", "1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t === "0" ? "Studio" : t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Parking slots <span className="text-slate-400 font-normal">(blank = unknown)</span></Label><Input data-testid="input-parking-slots" type="number" min="0" value={f.parking_slots} onChange={(e) => set("parking_slots", e.target.value)} placeholder="e.g. 1" className="mt-1.5" /></div>
          <div className="md:col-span-2">
            <Label>Completion Status</Label>
            <Select value={f.completion_status || NONE} onValueChange={(v) => set("completion_status", v === NONE ? "" : v)}><SelectTrigger data-testid="select-completion-status" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>— Not specified —</SelectItem>{COMPLETION_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>

          {/* ---- DESCRIPTION + Listing Assistant (always visible to eligible professionals on Create & Edit) ---- */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Description</Label>
              <div className="flex items-center gap-3"><AiBtn task="description" label="Draft description" /><AiBtn task="selling_points" label="Selling points" /></div>
            </div>
            <Textarea data-testid="input-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Highlights, view, turnover date…" className="mt-1.5" />
            {(aiPanel?.task === "description" || aiPanel?.task === "selling_points") && (
              <div data-testid="ai-panel-desc" className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
                {aiPanel.task === "description"
                  ? <p className="text-sm text-slate-800 whitespace-pre-wrap">{aiPanel.description}</p>
                  : <ul className="text-sm text-slate-800 list-disc pl-5 space-y-0.5">{(aiPanel.selling_points || []).map((s, i) => <li key={i}>{s}</li>)}</ul>}
                <div className="mt-2 flex items-center gap-3">
                  <button type="button" data-testid="ai-apply-desc" onClick={() => {
                    if (aiPanel.task === "description") set("description", aiPanel.description);
                    else set("description", ((f.description ? f.description + "\n\n" : "") + (aiPanel.selling_points || []).map((s) => `• ${s}`).join("\n")));
                    setAiPanel(null);
                  }} className="text-xs font-semibold text-primary hover:underline">Apply</button>
                  <button type="button" data-testid="ai-dismiss-desc" onClick={() => setAiPanel(null)} className="text-xs text-slate-500 hover:text-slate-700">Dismiss</button>
                  <span className="text-[11px] text-slate-400 ml-auto">AI-assisted draft — review before publishing.</span>
                </div>
              </div>
            )}
          </div>

          {/* ---- PROPERTY DETAILS & VISIBILITY (progressive disclosure) ---- */}
          <div className="md:col-span-2 rounded-xl border border-[#0F2C59]/25 bg-[#0F2C59]/[0.04] overflow-hidden">
            <button type="button" data-testid="toggle-more-details" onClick={() => setShowMore((s) => !s)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#0F2C59]/[0.07] transition-colors">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F2C59]/10 text-[#0F2C59] shrink-0"><SlidersHorizontal className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#0F2C59]">Property Details &amp; Visibility</span>
                <span className="block text-xs text-slate-500 mt-0.5">Add important property specifications and choose who can discover this listing.</span>
              </span>
              {!showMore && detailsCount > 0 && <span data-testid="details-count-indicator" className="shrink-0 text-xs font-semibold text-[#0F2C59]">{detailsCount} {detailsCount === 1 ? "detail" : "details"} added</span>}
              <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${showMore ? "rotate-180" : ""}`} />
            </button>
          </div>

          {showMore && (
            <>
              <div><Label>Neighborhood / district</Label><Input data-testid="input-neighborhood" value={f.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} placeholder="e.g. BGC" className="mt-1.5" /></div>
              <div>
                <Label>Bathrooms</Label>
                <Select value={f.bathrooms} onValueChange={(v) => set("bathrooms", v)}><SelectTrigger data-testid="select-bathrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["0", "1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label>Furnishing</Label>
                <Select value={f.furnishing} onValueChange={(v) => set("furnishing", v)}><SelectTrigger data-testid="select-furnishing" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Bare", "Semi-Furnished", "Fully Furnished"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Floor area (sqm)</Label><Input data-testid="input-area" type="number" min="0" step="any" value={f.floor_area_sqm} onChange={(e) => set("floor_area_sqm", e.target.value)} placeholder="78.5" className="mt-1.5" /></div>
              <div><Label>Lot area (sqm)</Label><Input data-testid="input-lot-area" type="number" min="0" step="any" value={f.lot_area_sqm} onChange={(e) => set("lot_area_sqm", e.target.value)} placeholder="e.g. 120.75" className="mt-1.5" /></div>
              <div className="md:col-span-2">
                <Label>Amenities &amp; features</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="amenities-picker">{AMENITIES.map((c) => (
                  <button key={c} type="button" data-testid={`amenity-${c.replace(/[^a-zA-Z]/g, "")}`} onClick={() => toggle(c)} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${f.amenities.includes(c) ? "bg-accent text-accent-foreground border-accent" : "bg-white border-border text-slate-600 hover:border-slate-400"}`}>{c}</button>
                ))}</div>
              </div>
            </>
          )}

          {!editId && (
            <div className="md:col-span-2" data-testid="create-visibility">
              <Label>Listing visibility</Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">Eligible listings publish to the Public Marketplace by default. You can change this any time.</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {[
                  { key: "PUBLIC_MARKETPLACE", label: "Public Marketplace", desc: "Visible to anyone browsing PropertyConnect.", icon: Globe },
                  { key: "NETWORK_ONLY", label: "Network Only", desc: "Visible only within PropertyConnect professional/network workflows.", icon: Users },
                  { key: "PRIVATE", label: "Private", desc: "Not publicly visible.", icon: Lock },
                ].map((o) => {
                  const active = f.visibility === o.key;
                  const Icon = o.icon;
                  return (
                    <button key={o.key} type="button" data-testid={`create-visibility-${o.key}`} onClick={() => set("visibility", o.key)}
                      className={`text-left rounded-xl border p-3 transition-colors ${active ? "border-accent bg-accent/5" : "border-border bg-white hover:border-slate-400"}`}>
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Icon className="h-4 w-4 text-primary" /> {o.label}
                        {active && <Check className="h-4 w-4 text-accent ml-auto" data-testid={`create-visibility-active-${o.key}`} />}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{o.desc}</div>
                    </button>
                  );
                })}
              </div>
              {isHosting && f.visibility === "PUBLIC_MARKETPLACE" && (
                <p className="text-[11px] text-slate-400 mt-2" data-testid="create-visibility-hosting-note">Public Stays listings require an active, verified Licensed Broker or Property Manager account — otherwise this saves as Network Only.</p>
              )}
            </div>
          )}

          <div className="md:col-span-2 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
            <Button data-testid="save-draft-property" onClick={saveDraft} disabled={savingDraft || saving} variant="outline" className="w-full sm:w-auto h-12 px-6 text-base font-semibold rounded-xl border-[#0F2C59]/30 text-[#0F2C59] hover:bg-[#0F2C59]/5 active:scale-95 transition-transform">{savingDraft ? "Saving…" : "Save Draft"}</Button>
            <Button data-testid="submit-property" onClick={submit} disabled={saving || savingDraft} className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 text-base font-semibold rounded-xl active:scale-95 transition-transform"><Sparkles className="h-5 w-5 mr-2" /> {saving ? "Saving…" : editId ? "Save changes" : "Find matching buyers"}</Button>
            {draftSavedAt && <span data-testid="draft-saved-note" className="text-xs text-slate-500 sm:ml-1">Draft saved · {new Date(draftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
          </div>
        </div>
      </Fade>
      <PostPublishDistribution
        open={!!distrib}
        onOpenChange={(o) => { if (!o) { const pid = distrib?.id; setDistrib(null); if (pid) nav(`/property/${pid}/matches`); } }}
        kind="property"
        id={distrib?.id}
        onDone={(url, view) => { const pid = distrib?.id; setDistrib(null); nav(view ? `/listing/${pid}` : `/property/${pid}/matches`); }}
      />
    </PageShell>
  );
}
